import { Inject, Injectable, OnInit } from '@angular/core';
import { IClientSettings, IPIMSettingItem } from '../interfaces';
import { DebugService } from './debug.service';

interface NewCategory {
  categoryCode: string;
  roomCodes?: string[];
  name: { [lang: string]: string }; // { fi: "Huvilat", en: "Villas", ... }
}

interface OldCategoryName {
  lang: string;
  text: string;
}

interface OldCategory {
  value: string;                 // old: "value"
  name: OldCategoryName[];       // old: [{lang,text}]
  roomCodes?: string[] | null;   // old schema allows null/array
}

interface NewEndpoint {
  cloudId: string;
  id: string;
}

interface OldEndpoint {
  hotelCode: string;
  endpoint?: string;
}

@Injectable({
  providedIn: 'root'
})


export class PIMSettingsMapperService {
  
  constructor(private debug: DebugService) {}

  mergeSettings(oldSettings: IClientSettings | null | undefined, newSettings: IPIMSettingItem[]): IClientSettings {
    // start from old settings as base
    const baseOld = oldSettings ?? {};   // <-- IMPORTANT

    const merged: IClientSettings = { ...baseOld };

    for (const item of newSettings) {
      const newKey = item.settingName.name;
      const oldKey = this.mapNewSettingNameToOldKey(newKey);

      this.debug.log('PIMSettingsMapperService: mapping setting', { newKey, oldKey, rawValue: item.settingValue });

      if (!oldKey) continue;

      // SPECIAL CASES

      if (oldKey === 'searchMask.categories') {
        merged[oldKey] = this.mapCategoriesFromNewToOld(
          item.settingValue,
          baseOld['searchMask.categories'] as any[] | undefined
        );
        continue;
      }

      if (oldKey === 'profile.termsAndConditionsUrls') {
        merged[oldKey] = this.mapTermsUrlsFromNewToOld(
          item.settingValue,
          baseOld['profile.termsAndConditionsUrls'] as any[] | undefined
        );
        continue;
      }

      if (oldKey === 'giftcardTermsUrls') {
        merged[oldKey] = this.mapTermsUrlsFromNewToOld(
          item.settingValue,
          baseOld['giftcardTermsUrls'] as any[] | undefined
        );
        continue;
      }

      if (oldKey === 'app.endpoints') {
        merged[oldKey] = this.mapEndpointsFromNewToOld(
          item.settingValue,
          baseOld['app.endpoints'] as any[] | undefined
        );
        continue;
      }

      if (oldKey === 'searchMask.usePmsCategories') {
        merged[oldKey] = this.mapUsePmsCategories(
          item.settingValue,
          baseOld['searchMask.usePmsCategories'] as boolean | undefined
        );
        continue;
      }

      if (
        oldKey === 'profile.etaMinHour' ||
        oldKey === 'profile.etaMaxHour' ||
        oldKey === 'profile.etaStep'
      ) {
        merged[oldKey] = this.mapEtaAsString(
          item.settingValue,
          baseOld[oldKey] as string | undefined
        );
        continue;
      }

      // DEFAULT PATH: just parse by valueType
      const parsed = this.parseSettingValue(
        item.settingValue,
        item.settingName.valueType
      );

      merged[oldKey] = parsed === null ? baseOld[oldKey] ?? null : parsed;
    }
    

    return merged;
  }

  private mapNewSettingNameToOldKey(name: string): string | null {
    // New → Old rename rules

    // 1. datePicker.showAvailability
    if (name === "searchmask.datePickerShowAvailability") {
      return "datePicker.showAvailability";
    }

    // 2. Prefix rename: searchmask.* → searchMask.*
    if (name.startsWith("searchmask.")) {
      return "searchMask." + name.substring("searchmask.".length);
    }

    if (name === 'wbeLanguages') {
      return 'app.languages';
    }

    if (name === 'GiftcardPrimaryUrls') {
      return 'app.giftCardShopUrl'
    }

    if (name === 'ticketRateId') {
      return 'profile.externalExtraId';
    }

    // 3. app.* and profile.* and availability.* have matching names
    return name;
  }

  private parseSettingValue(raw: string, valueType: number): any {
    if (raw == null || raw === 'null') return null;

    switch (valueType) {
      case 1: { // string
        const lower = raw.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;

        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed === 'string') return parsed;
        } catch {}
        return raw;
      }

      case 3: { // boolean-ish
        const lower = raw.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;

        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed === 'boolean') return parsed;
          return parsed;
        } catch {
          return raw;
        }
      }

      case 4: { // number
        let v: any = raw;
        try {
          v = JSON.parse(raw); // handles "\"2\"" and "3"
        } catch {}
        const n = Number(v);
        return isNaN(n) ? v : n;
      }

      case 6: { // JSON / collection
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
      }

      default:
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
    }
  }

    // --- searchMask.categories ---

  private mapCategoriesFromNewToOld(rawValue: string, oldCategories?: OldCategory[]): OldCategory[] {
    let newCategories: NewCategory[];

    try {
      newCategories = JSON.parse(rawValue);
      if (!Array.isArray(newCategories)) return [];

      // filter out categories that have no meaningful data
      const meaningfulCategories = newCategories.filter(c => {
        const hasCategoryCode = (c.categoryCode ?? "").trim().length > 0;

        return hasCategoryCode;
      });


      if (meaningfulCategories.length === 0) {
        return [];
      }

    } catch {
      // if PIM sends something unexpected, keep old config as fallback
      return oldCategories ?? [];
    }

     const converted = newCategories.map(c => ({
      value: c.categoryCode,
      roomCodes: c.roomCodes ?? null,
      name: Object.entries(c.name || {}).map(([lang, text]) => ({
        lang,
        text: String(text)
      }))
    }));

    return converted;
  }

  // --- profile.termsAndConditionsUrls ---

  private mapTermsUrlsFromNewToOld(
    rawValue: string,
    oldValue?: { lang: string; url: string }[]
  ) {
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed).map(([lang, url]) => ({
          lang,
          url: String(url ?? '')
        }));
      }
    } catch {}

    return oldValue ?? [];
  }

  // --- app.endpoints ---


  private mapEndpointsFromNewToOld(
    rawValue: string,
    oldValue?: OldEndpoint[]
  ): OldEndpoint[] {
    let newEndpoints: NewEndpoint[];

    try {
      newEndpoints = JSON.parse(rawValue);
      if (!Array.isArray(newEndpoints)) return oldValue ?? [];
    } catch {
      return oldValue ?? [];
    }

    // If old endpoints missing → build minimal valid objects
    if (!oldValue || oldValue.length === 0) {
      return newEndpoints.map(e => ({ hotelCode: e.cloudId }));
    }

    // Otherwise preserve endpoint URL
    return newEndpoints.map((e, idx) => ({
      ...oldValue[Math.min(idx, oldValue.length - 1)],
      hotelCode: e.cloudId
    }));
  }

  // --- searchMask.usePmsCategories ---

  private mapUsePmsCategories(
    rawValue: string,
    oldValue?: boolean
  ): boolean {
    let val = rawValue;

    try {
      const parsed = JSON.parse(rawValue);
      if (typeof parsed === 'string') val = parsed;
    } catch {}

    const upper = String(val).toUpperCase().trim();

    switch (upper) {
      case "PMS": return true;
      case "PIM": return false;
      case "NA": return false;
    }

    return oldValue ?? false;
  }



  // --- profile.eta* (strings in old schema) ---

  private mapEtaAsString(rawValue: string, oldValue?: string): string {
    const n = Number(rawValue);
    if (!isNaN(n)) return String(n);
    return oldValue ?? String(rawValue);
  }
}